import type { KnowledgeArticle } from "@/types/knowledgeBase";
import { knowledgeCategories } from "@/types/knowledgeBase";

interface ArticleListProps {
  articles: KnowledgeArticle[];
  selectedId: string | null;
  searchQuery: string;
  onSearch: (q: string) => void;
  activeCategory: string;
  activeSubCategory: string;
  onSelectCategory: (id: string) => void;
  onSelectSubCategory: (sub: string) => void;
  onSelectArticle: (id: string) => void;
}

export default function ArticleList({
  articles,
  selectedId,
  searchQuery,
  onSearch,
  activeCategory,
  activeSubCategory,
  onSelectCategory,
  onSelectSubCategory,
  onSelectArticle,
}: ArticleListProps) {
  const currentCategory = knowledgeCategories.find((c) => c.id === activeCategory);
  const subCategories = ["全部", ...(currentCategory?.subCategories || [])];

  const getCategoryLabel = (id: string) => {
    const found = knowledgeCategories.find((c) => c.id === id);
    return found ? found.label : id;
  };

  const getCategoryColor = (catId: string) => {
    if (catId === "product") return "bg-secondary-100 text-secondary-700";
    if (catId === "logistics") return "bg-accent-100 text-accent-700";
    if (catId === "employee") return "bg-primary-100 text-primary-700";
    if (catId === "wine") return "bg-foreground-100 text-foreground-700";
    return "bg-primary-100 text-primary-700";
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-4 pt-4 pb-3">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="搜尋問題、答案、編號、標題..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="px-4 pb-3">
        <div className="flex items-center gap-2">
          {knowledgeCategories.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => onSelectCategory(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat.id === "all" && <i className="ri-apps-line" />}
                {cat.id === "product" && <i className="ri-shopping-bag-3-line" />}
                {cat.id === "logistics" && <i className="ri-truck-line" />}
                {cat.id === "employee" && <i className="ri-team-line" />}
                {cat.id === "wine" && <i className="ri-goblet-line" />}
                <span>
                  {cat.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Sub Category Tags */}
      <div className="px-4 pb-3">
        <div className="flex flex-wrap gap-1.5">
          {subCategories.map((sub) => {
            const isActive = activeSubCategory === sub;
            return (
              <button
                key={sub}
                onClick={() => onSelectSubCategory(sub)}
                className={`px-2.5 py-1 rounded-full text-xs transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? "bg-foreground-800 text-background-50"
                    : "bg-background-100 text-foreground-500 hover:bg-background-200"
                }`}
              >
                {sub}
              </button>
            );
          })}
        </div>
      </div>

      {/* Article List */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        {articles.length === 0 && (
          <div className="text-center py-12 text-foreground-400 text-sm">
            <i className="ri-inbox-line text-2xl mb-2 block" />
            找不到相關文章
          </div>
        )}
        {articles.map((article) => {
          const isSelected = selectedId === article.id;
          return (
            <button
              key={article.id}
              onClick={() => onSelectArticle(article.id)}
              className={`w-full text-left rounded-xl border transition-all cursor-pointer p-3 ${
                isSelected
                  ? "border-primary-300 bg-primary-50/60 ring-1 ring-primary-300"
                  : "border-background-200 bg-background-50 hover:border-background-300 hover:bg-background-100/50"
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-mono text-foreground-400">
                  {article.article_code}
                </span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getCategoryColor(
                    article.category
                  )}`}
                >
                  {getCategoryLabel(article.category)}
                </span>
              </div>
              <h4
                className={`text-sm font-semibold leading-snug mb-1 ${
                  isSelected ? "text-primary-700" : "text-foreground-900"
                }`}
              >
                {article.title}
              </h4>
              <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                {article.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}